import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import type { SxProps, Theme } from '@mui/material/styles'
import React from 'react'

import { borderRadius, spacing } from '../KestrixDesignSystem.jsx'
import colorTokens from '../tokens/colors.js'
import { metricCardShellLayoutSx } from './styles/kestrix-metric-card-shell.js'

const n = colorTokens.global.neutral

/**
 * Inner skeleton rows for use inside {@link KestrixMetricCard} (parent
 * supplies shell + gap). Mirrors the loaded-state two-row layout: a small
 * title shimmer above a value + delta shimmer.
 */
export const KestrixMetricCardSkeletonContent = (): React.ReactElement => (
  <>
    <Skeleton
      variant="rounded"
      width={96}
      height={16}
      sx={{ bgcolor: n[100], borderRadius: borderRadius.sm }}
    />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: spacing[2],
        width: '100%'
      }}
    >
      <Skeleton
        variant="rounded"
        width={80}
        height={24}
        sx={{ bgcolor: n[200], borderRadius: borderRadius.sm }}
      />
      <Skeleton
        variant="rounded"
        width={48}
        height={14}
        sx={{ bgcolor: n[100], borderRadius: borderRadius.sm }}
      />
    </Box>
  </>
)

export type KestrixMetricCardSkeletonProps = {
  sx?: SxProps<Theme>
} & Omit<React.ComponentPropsWithoutRef<typeof Box>, 'sx'>

/** Standalone metric card loading placeholder (full KDS loading chrome). */
export const KestrixMetricCardSkeleton = ({
  sx,
  ...boxProps
}: KestrixMetricCardSkeletonProps) => (
  <Box
    className="kestrix-metric-card-root"
    data-testid={`kestrix-metric-card-skeleton-${boxProps.id}`}
    sx={{
      ...metricCardShellLayoutSx,
      backgroundColor: n[500],
      borderColor: n[100],
      ...sx
    }}
    {...boxProps}
  >
    <KestrixMetricCardSkeletonContent />
  </Box>
)

export default KestrixMetricCardSkeleton
